import bannerAtendimento from '@/assets/banner_atendimento.webp';
import { Banner, BannerTitulo } from '@/components/banner';
import {
  esconderChatAtendimento,
  mostrarChatAtendimento,
  useOctadesk,
} from '@/hooks/useOctadesk';
import { useSessao } from '@/lib/auth';
import { env } from '@/lib/env';
import { useTranslation } from '@/lib/i18n';
import { enabledPageGuard, isPageEnabled } from '@/lib/migration';
import { cn } from '@/lib/utils';
import { montarUrlWhatsAppAtendimento } from '@/lib/whatsapp';
import { createFileRoute } from '@tanstack/react-router';
import Groups from '~icons/material-symbols/groups';
import HelpOutline from '~icons/material-symbols/help-outline';
import School from '~icons/material-symbols/school';
import CardChat from './-card-chat';
import CardWhatsApp from './-card-whatsapp';
import LinkAjudaRapida from './-link-ajuda-rapida';

const perguntasFrequentesUrl =
  env.VITE_SITE_SECULLUM_URL +
  env.VITE_SITE_SECULLUM_CAMINHO_PERGUNTAS_FREQUENTES;

export const Route = createFileRoute('/__authenticated/atendimento/')({
  beforeLoad: async () => {
    await enabledPageGuard('atendimento');
  },
  component: RouteComponent,
  onEnter: mostrarChatAtendimento,
  onLeave: esconderChatAtendimento,
});

function RouteComponent() {
  const { t } = useTranslation('private');

  const { data: usuario } = useSessao();
  const { abrirChat } = useOctadesk();

  const possuiTreinamento =
    usuario?.permissoes.includes('Treinamentos') ?? false;

  const whatsappEnabled = isPageEnabled('atendimento.whatsapp');

  const whatsappUrl = montarUrlWhatsAppAtendimento({
    revendaNome: usuario?.revendaNome,
    revendaCnpj: usuario?.revendaCnpj,
    email: usuario?.email,
  });

  return (
    <main className="flex-1">
      <Banner imagemUrl={bannerAtendimento} height="sm">
        <BannerTitulo>{t($ => $.atendimento.titulo)}</BannerTitulo>
      </Banner>

      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <h2 className="mb-10 text-center text-xl font-semibold text-muted-foreground">
          {t($ => $.atendimento.subtitulo)}
        </h2>

        <div
          className={cn(
            'grid grid-cols-1 gap-8',
            whatsappEnabled && 'sm:grid-cols-2',
          )}
        >
          {whatsappEnabled && (
            <CardWhatsApp
              descricao={t($ => $.atendimento.whatsappDescricao)}
              textoBotao={t($ => $.atendimento.whatsapp)}
              href={whatsappUrl}
            />
          )}

          <CardChat
            descricao={t($ => $.atendimento.chatDescricao)}
            textoBotao={t($ => $.atendimento.chat)}
            onClick={abrirChat}
          />
        </div>
      </div>

      <div className="mt-20 border-t bg-muted/40 py-12">
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 sm:flex-row sm:items-center">
          <p className="shrink-0 text-lg font-semibold text-secullum-yellow sm:w-36">
            {t($ => $.atendimento.ajudaRapida)}
          </p>
          <div className="grid flex-1 grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-4">
            <LinkAjudaRapida
              icone={HelpOutline}
              titulo={t($ => $.atendimento.perguntasFrequentes)}
              descricao={t($ => $.atendimento.perguntasFrequentesDescricao)}
              href={perguntasFrequentesUrl}
            />
            {possuiTreinamento && (
              <LinkAjudaRapida
                icone={School}
                titulo={t($ => $.atendimento.treinamentos)}
                descricao={t($ => $.atendimento.treinamentosDescricao)}
                to="/suporte/treinamentos"
              />
            )}
            <LinkAjudaRapida
              icone={Groups}
              titulo={t($ => $.atendimento.comunidadeWhatsApp)}
              descricao={t($ => $.atendimento.comunidadeWhatsAppDescricao)}
              href={env.VITE_URL_COMUNIDADE_WHATSAPP}
            />
          </div>
        </div>
      </div>
    </main>
  );
}
